// src/services/auctionService.ts
import apiClient from './api'
import type { Auction, AuctionFilters, Bid, Category, PaginatedResponse } from '@/types'

export const auctionService = {
  async getAuctions(filters: AuctionFilters = {}): Promise<PaginatedResponse<Auction>> {
    const res = await apiClient.get<PaginatedResponse<Auction>>('/api/auctions', filters)
    return res.data
  },

  async getAuctionById(id: string): Promise<{ success: boolean; data: Auction }> {
    const res = await apiClient.get<{ success: boolean; data: Auction }>(`/api/auctions/${id}`)
    return res.data
  },

  async createAuction(data: any): Promise<{ success: boolean; data: Auction }> {
    const res = await apiClient.post<{ success: boolean; data: Auction }>('/api/auctions', data)
    return res.data
  },

  async updateAuction(id: string, data: any): Promise<{ success: boolean; data: Auction }> {
    const res = await apiClient.put<{ success: boolean; data: Auction }>(`/api/auctions/${id}`, data)
    return res.data
  },

  async deleteAuction(id: string): Promise<{ success: boolean }> {
    const res = await apiClient.delete<{ success: boolean }>(`/api/auctions/${id}`)
    return res.data
  },

  // Re-publish an ended-unsold lot as a fresh auction (same content, new
  // clock). The server refuses anything but the seller's own ended lots and
  // a lot that already has a live relist.
  async relistAuction(id: string, body: { durationHours?: number } = {}): Promise<{ success: boolean; data: Auction; error?: string }> {
    const res = await apiClient.post<{ success: boolean; data: Auction; error?: string }>(`/api/auctions/${id}/relist`, body)
    return res.data
  },

  // The only way a lot's price can move downward: bidding only ever raises
  // it. The server enforces the real rules (stays above the current bid,
  // meets the admin's minimum-drop threshold); this just carries the number.
  async lowerPrice(id: string, newPrice: number): Promise<{ success: boolean; data: Auction; error?: string }> {
    const res = await apiClient.post<{ success: boolean; data: Auction; error?: string }>(`/api/auctions/${id}/lower-price`, { newPrice })
    return res.data
  },

  async placeBid(auctionId: string, amount: number): Promise<{ success: boolean; data: Bid; auction: Auction }> {
    const res = await apiClient.post<{ success: boolean; data: Bid; auction: Auction }>(`/api/auctions/${auctionId}/bids`, {
      amount,
    })
    return res.data
  },

  async getAuctionBids(auctionId: string): Promise<{ success: boolean; data: Bid[] }> {
    const res = await apiClient.get<{ success: boolean; data: Bid[] }>(`/api/auctions/${auctionId}/bids`)
    return res.data
  },

  async buyNow(auctionId: string): Promise<{ success: boolean; data: Auction; paymentId: string }> {
    const res = await apiClient.post<{ success: boolean; data: Auction; paymentId: string }>(`/api/auctions/${auctionId}/buy-now`)
    return res.data
  },

  async getCategories(): Promise<{ success: boolean; data: Category[] }> {
    const res = await apiClient.get<{ success: boolean; data: Category[] }>('/api/categories')
    return res.data
  },

  async getRegions(): Promise<{ success: boolean; data: any[] }> {
    const res = await apiClient.get<{ success: boolean; data: any[] }>('/api/regions')
    return res.data
  },

  async reportAuction(auctionId: string, data: {
    reason?: string
    reasonCategory?: string
    details?: string
    role?: 'buyer' | 'seller'
    respondentId?: string
  }): Promise<{ success: boolean; data: any; message?: string }> {
    const res = await apiClient.post<{ success: boolean; data: any; message?: string }>(`/api/auctions/${auctionId}/report`, data)
    return res.data
  },
}

export default auctionService

